import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import type { ContactNote } from '@/types/contact';

function normalizeContactNote(raw: Record<string, unknown>): ContactNote {
  const rawAttachments = Array.isArray(raw.attachments) ? raw.attachments : [];
  return {
    id: String(raw.id ?? ''),
    content: String(raw.content ?? ''),
    createdBy: raw.createdBy ? String(raw.createdBy) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    pinned: Boolean(raw.pinned),
    attachments: rawAttachments.map((item) => {
      const a = item as Record<string, unknown>;
      return {
        id: String(a.id ?? ''),
        fileName: String(a.fileName ?? 'Attachment'),
        mimeType: String(a.mimeType ?? 'application/octet-stream'),
        fileSize: Number(a.fileSize ?? 0),
        downloadUrl: a.downloadUrl ? String(a.downloadUrl) : undefined,
      };
    }),
  };
}

export function useContactNotes(contactId: string) {
  return useApiQuery<ContactNote[]>({
    queryKey: contactKeys.notes(contactId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/notes`,
      );
      const notes = Array.isArray(raw.data)
        ? raw.data
        : Array.isArray(raw.items)
          ? raw.items
          : Array.isArray(raw)
            ? raw
            : [];
      return notes.map((note) => normalizeContactNote(note as Record<string, unknown>));
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
    // No client-side caching — notes can change from QB at any time and
    // the operator expects to always see the live state. Forces a fresh
    // request on every mount, focus, reconnect, and 5-second poll while
    // the page is open. `gcTime: 0` keeps stale data from lingering in
    // the cache between mounts.
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 5_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
}

export function useAddContactNote(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactNote, { content: string }>({
    mutationFn: (dto) =>
      apiPost<ContactNote, { content: string }>(
        operationsClient,
        `/contacts/${contactId}/notes`,
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: contactKeys.notes(contactId),
      });
      toast.success('Note added');
    },
  });
}

export function useUpdateContactNote(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactNote, { noteId: string; content: string }>({
    mutationFn: ({ noteId, content }) =>
      apiPatch<ContactNote, { content: string }>(
        operationsClient,
        `/contacts/${contactId}/notes/${noteId}`,
        { content },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: contactKeys.notes(contactId),
      });
      toast.success('Note updated');
    },
  });
}

export function useSetContactNotePinned(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactNote, { noteId: string; pinned: boolean }>({
    mutationFn: ({ noteId, pinned }) =>
      apiPatch<ContactNote, { pinned: boolean }>(
        operationsClient,
        `/contacts/${contactId}/notes/${noteId}`,
        { pinned },
      ),
    onSuccess: (_data, { pinned }) => {
      void queryClient.invalidateQueries({
        queryKey: contactKeys.notes(contactId),
      });
      toast.success(pinned ? 'Note pinned' : 'Note unpinned');
    },
  });
}

export function useDeleteContactNote(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: (noteId) =>
      apiDelete<{ deleted: boolean }>(operationsClient, `/contacts/${contactId}/notes/${noteId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: contactKeys.notes(contactId),
      });
      toast.success('Note deleted');
    },
  });
}
